// vg-binding: P7.D-01
// vg-binding: P7.D-04
//
// apps/vendor/src/features/dashboard/KpiCardGrid.tsx
//
// 6-card KPI grid. Each card shows the metric value + label + a colour
// dot derived from `direction` × threshold (higher_better above 90 = green,
// below 70 = red; lower_better inverts).

import { useTranslation } from 'react-i18next';

import { cn } from '@printway/ui';

import type { KpiMetric } from './api.js';

interface Props {
  metrics: KpiMetric[];
  isLoading?: boolean;
}

function classifyTier(m: KpiMetric): 'good' | 'warn' | 'bad' | 'empty' {
  if (m.data_source === 'placeholder') return 'empty';
  if (m.direction === 'higher_better') {
    if (m.value >= 90) return 'good';
    if (m.value >= 70) return 'warn';
    return 'bad';
  }
  // lower_better
  if (m.value <= 5) return 'good';
  if (m.value <= 15) return 'warn';
  return 'bad';
}

const TIER_DOT: Record<'good' | 'warn' | 'bad' | 'empty', string> = {
  good: 'bg-green-500',
  warn: 'bg-yellow-500',
  bad: 'bg-red-500',
  empty: 'bg-pw-border',
};

function SkeletonCard(): JSX.Element {
  return (
    <div className="h-28 animate-pulse rounded-xl border border-pw-border bg-pw-surface" />
  );
}

export function KpiCardGrid({ metrics, isLoading }: Props): JSX.Element {
  const { t } = useTranslation('dashboard');
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_v, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
      {metrics.map((m) => {
        const tier = classifyTier(m);
        const isPlaceholder = m.data_source === 'placeholder';
        return (
          <div
            key={m.id}
            data-testid={`dashboard-kpi-${m.id}`}
            className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <p className="text-sm text-pw-text-muted">{m.label}</p>
              <span
                aria-hidden="true"
                className={cn('h-2.5 w-2.5 rounded-full', TIER_DOT[tier])}
              />
            </div>
            {isPlaceholder ? (
              <>
                <p className="mt-2 text-3xl font-semibold text-pw-text-muted">
                  —
                  <span className="ml-1 text-base font-normal text-pw-text-muted">
                    {m.unit}
                  </span>
                </p>
                <p className="mt-1 text-xs text-pw-text-muted">
                  {t('kpi.noData')}
                </p>
              </>
            ) : (
              <p className="mt-2 text-3xl font-semibold text-pw-text">
                {m.value}
                <span className="ml-1 text-base font-normal text-pw-text-muted">
                  {m.unit}
                </span>
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default KpiCardGrid;
